import { useState } from "react";
import useSWR from "swr";
import { ApiError, apiFetch, swrFetcher, type AdminTeam } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatUsd } from "@/lib/format";

export function CreditsTab() {
  const { data: teams, isLoading, mutate } = useSWR<AdminTeam[]>("/api/admin/teams", swrFetcher);
  const [topupTeamId, setTopupTeamId] = useState<string | null>(null);
  const [topupAmount, setTopupAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submitTopup = async () => {
    if (!topupTeamId || !topupAmount) return;
    const amount = Number(topupAmount);
    if (!amount || amount <= 0) return;
    setSubmitting(true);
    setError(null);
    try {
      // Unhandled before: a 403/422 left the form open with no feedback, so an
      // admin could believe the wallet had been funded when it had not.
      await apiFetch(`/api/admin/wallet/${topupTeamId}/topup`, {
        method: "POST",
        body: JSON.stringify({ amount_usd: amount, reason: "Admin wallet funding" }),
      });
      setTopupTeamId(null);
      setTopupAmount("");
      mutate();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Top-up failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading || !teams) return <p className="text-sm text-studio-muted">Loading…</p>;

  return (
    <div>
      <h1 className="mb-5 font-display text-xl font-bold tracking-tight sm:mb-6 sm:text-2xl">USD Wallet Management</h1>
      {error && (
        <p className="mb-4 rounded-lg border border-studio-danger/30 bg-studio-danger/10 px-3 py-2 text-sm text-studio-danger">
          {error}
        </p>
      )}
      <div className="space-y-4">
        {teams.map((t) => (
          <Card key={t.id} className="flex flex-col items-stretch justify-between gap-4 p-4 sm:flex-row sm:flex-wrap sm:items-center">
            <div>
              <h3 className="font-display font-semibold">{t.name}</h3>
              <p className="text-xs text-studio-faint">{t.member_count} member(s)</p>
            </div>
            <div className="sm:text-right">
              <p className="font-mono text-2xl font-bold text-studio-success">{formatUsd(t.wallet_balance_usd, { decimals: 2 }) ?? "—"}</p>
              <p className="font-mono text-xs text-studio-faint">{formatUsd(t.wallet_held_usd, { decimals: 2 }) ?? "—"} held</p>
            </div>
            <div>
              {topupTeamId === t.id ? (
                <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
                  <Input
                    type="number"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    placeholder="USD amount"
                    className="col-span-2 w-full sm:col-span-1 sm:w-32"
                  />
                  <Button size="sm" onClick={submitTopup} loading={submitting}>
                    Confirm
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setTopupTeamId(null)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setTopupTeamId(t.id);
                    setTopupAmount("");
                  }}
                >
                  + Top up
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
